import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminStats, getBookings } from "@/api";
import { fmtMxn, StatusBadge } from "./adminShared";
import BookingDetail from "./BookingDetail";
import { TrendingUp, CalendarClock, Hourglass, CheckCircle2, Users, XCircle } from "lucide-react";

function KpiCard({ icon: Icon, label, value, accent = "#C4A661", testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C1310] border border-[#1E2B25] p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#A3A095]">{label}</span>
        <Icon size={16} strokeWidth={1.5} style={{ color: accent }} />
      </div>
      <p className="font-serif-hc text-3xl leading-none" style={{ color: accent }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => {
    getAdminStats().then(setStats).catch(() => {});
    getBookings().then(setBookings).catch(() => {});
  };
  useEffect(load, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings
    .filter((b) => b.date >= today && !["cancelled", "completed"].includes(b.status))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 6);

  const onUpdated = (updated, deletedId) => {
    if (deletedId) setBookings((bs) => bs.filter((x) => x.id !== deletedId));
    else {
      setBookings((bs) => bs.map((x) => (x.id === updated.id ? updated : x)));
      setSelected(updated);
    }
    getAdminStats().then(setStats).catch(() => {});
  };

  return (
    <div data-testid="admin-dashboard">
      <p className="eyebrow mb-2">Centro de operaciones</p>
      <h1 className="font-serif-hc font-light text-3xl md:text-4xl text-[#F5F2EA]">Dashboard</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard testid="kpi-month-sales" icon={TrendingUp} label="Ventas del mes" value={fmtMxn(stats?.month_sales_mxn)} />
        <KpiCard testid="kpi-upcoming" icon={CalendarClock} label="Reservas próximas" value={stats?.upcoming_count ?? "—"} accent="#139090" />
        <KpiCard testid="kpi-pending" icon={Hourglass} label="Pendientes de pago" value={stats?.pending_payment ?? "—"} />
        <KpiCard testid="kpi-confirmed" icon={CheckCircle2} label="Confirmadas" value={stats?.confirmed ?? "—"} accent="#139090" />
        <KpiCard testid="kpi-travelers" icon={Users} label="Viajeros próximos" value={stats?.upcoming_travelers ?? "—"} accent="#F5F2EA" />
        <KpiCard testid="kpi-cancelled" icon={XCircle} label="Cancelaciones" value={stats?.cancelled ?? "—"} accent="#8B8778" />
      </div>

      <div className="mt-10 flex items-end justify-between gap-4">
        <h2 className="font-serif-hc text-2xl text-[#F5F2EA]">Próximas reservas</h2>
        <Link to="/admin/reservas" data-testid="dashboard-see-all" className="text-[11px] tracking-[0.2em] uppercase text-[#C4A661] hover:text-[#F5F2EA] transition-colors">
          Ver todas →
        </Link>
      </div>

      <div className="mt-4 border border-[#1E2B25] divide-y divide-[#1E2B25]">
        {upcoming.map((b) => (
          <button
            key={b.id}
            data-testid={`dashboard-upcoming-${b.id}`}
            onClick={() => setSelected(b)}
            className="w-full flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#0C1310]/70 transition-colors"
          >
            <div>
              <p className="text-[#F5F2EA] text-sm">{b.name}</p>
              <p className="text-xs text-[#A3A095] mt-0.5">{b.tour_name}</p>
            </div>
            <div className="flex items-center gap-5">
              <span className="text-sm text-[#F5F2EA] whitespace-nowrap">{b.date}</span>
              <span className="text-sm text-[#C4A661] whitespace-nowrap">{fmtMxn(b.total_price_mxn)}</span>
              <StatusBadge status={b.status} />
            </div>
          </button>
        ))}
        {!upcoming.length && <p className="p-8 text-center text-[#A3A095] text-sm" data-testid="dashboard-no-upcoming">No hay reservas próximas.</p>}
      </div>

      <BookingDetail booking={selected} open={!!selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />
    </div>
  );
}
